import { Component, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
	selector: 'app-translate-renderer',
	template: `<span [attr.title]="hasTooltip ? fullValue : null">{{ displayValue }}</span>`,
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TranslateRendererComponent {
	translatedValue: string;
	displayValue: string = '';
	fullValue: string = '';
	hasTooltip = false;

	constructor(private _translateService: TranslateService) {}

	agInit(params: any): void {
		this.fullValue = params.value || '';
		this.displayValue = this.fullValue;

		const maxLength = params.maxLength || params.colDef?.cellRendererParams?.maxLength;

		if (params.value !== undefined && params.value !== null && params.value !== '') {
			this.translateCell(params).subscribe(translation => {
				this.translatedValue = translation;
				this.fullValue = translation || '';

				if (maxLength && this.fullValue && this.fullValue.length > maxLength) {
					this.displayValue = this.fullValue.substring(0, maxLength) + '...';
					this.hasTooltip = true;
				} else {
					this.displayValue = this.fullValue;
					this.hasTooltip = false;
				}
			});
		}
	}

	private translateCell(params: any): Observable<string> {
		const originalValue = params.value !== undefined && params.value !== null ? String(params.value) : '';
		const entityKey = this.resolveTranslationKey(params);

		if (entityKey) {
			return this._translateService.stream(entityKey).pipe(
				map(translated => {
					// If the translation key was not found by ngx-translate, it returns the key itself.
					// In that case, fall back to the original DB text value.
					if (!translated || translated === entityKey) {
						return originalValue;
					}
					return translated;
				})
			);
		}

		if (originalValue) {
			return this._translateService.stream(originalValue).pipe(map(translated => translated || originalValue));
		}

		return of('');
	}

	private resolveTranslationKey(params: any): string | null {
		const explicitKey = params.colDef?.cellRendererParams?.translationKey;
		if (explicitKey) {
			return typeof explicitKey === 'function' ? explicitKey(params.data) : explicitKey;
		}

		const explicitIdProperty = params.colDef?.cellRendererParams?.idProperty;
		if (explicitIdProperty && params.data) {
			const id = this.getNestedProperty(params.data, explicitIdProperty);
			if (id) return this.formatId(id);
		}

		const field = params.colDef?.field;
		if (!field || !params.data) {
			return params.data?.id ? this.formatId(params.data.id) : null;
		}

		const isDescription = field === 'description' || field.endsWith('.description');

		if (field.includes('.')) {
			const parts = field.split('.');
			parts.pop(); // remove last property (e.g. 'name' or 'description')
			const parentPath = parts.join('.');
			const parentObj = this.getNestedProperty(params.data, parentPath);

			if (parentObj && parentObj.id) {
				const id = this.formatId(parentObj.id);
				if (isDescription) {
					const descKey = `${id}_description`;
					return this._translateService.instant(descKey) !== descKey ? descKey : id;
				}
				return id;
			}

			// Also check if flat parent_id exists (e.g. activity_id)
			const flatIdKey = `${parts[parts.length - 1]}_id`;
			if (params.data[flatIdKey]) {
				const id = this.formatId(params.data[flatIdKey]);
				if (isDescription) {
					const descKey = `${id}_description`;
					return this._translateService.instant(descKey) !== descKey ? descKey : id;
				}
				return id;
			}
		}

		// Top-level field
		if (params.data.id) {
			const id = this.formatId(params.data.id);
			if (isDescription) {
				const descKey = `${id}_description`;
				return this._translateService.instant(descKey) !== descKey ? descKey : id;
			}
			return id;
		}

		return null;
	}

	private formatId(id: any): string {
		if (!id) return '';
		const str = String(id);
		return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str) ? str.toUpperCase() : str;
	}

	private getNestedProperty(obj: any, path: string): any {
		if (!obj || !path) return null;
		return path.split('.').reduce((prev, curr) => (prev ? prev[curr] : null), obj);
	}
}
